import React, { useEffect, useState } from "react";
import { getUserDataAPI } from "../../service/UserService";
import store from "../../redux/store";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { PERMISSION_MAP } from "../../utils/constants";
import PasswordCheckCasino from "./PasswordCheckCasino";

export default function Sidebar() {
  const location = useLocation();
  const [userName, setUserName] = useState("");
  const dispatch = useDispatch();
  const userData = store.getState().account.userData;
  const _isMounted = React.useRef(true);

  // ── Password-modal state ──────────────────────────────────────────
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingUrl, setPendingUrl] = useState("");

  /**
   * Navigate to `url`, but if the item requires a password AND the current
   * user is SUPER_ADMIN, show the password modal first.
   */
  const handleNavClick = (url, requiresPassword = false) => {
    if (requiresPassword && userData?.userType === "SUPER_ADMIN") {
      setPendingUrl(url);
      setShowPasswordModal(true);
    } else {
      navigate(url);
      closeSideDrawer();
    }
  };

  const getAccountDetails = async () => {
    const { response } = await getUserDataAPI(
      store.getState().account.userData.id
    );
    dispatch({
      type: "accountData/setUserProfile",
      payload: response,
    });
  };

  useEffect(() => {
    _isMounted.current = true;
    return () => {
      _isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (_isMounted.current) {
      setUserName(store.getState().account.userData.username);
      const interval = setInterval(() => {
        getAccountDetails();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, []);

  // Keep breakpoint consistent with Header/App breakpoints (AdminLTE mobile)
  const [isMobileUI, setIsMobileUI] = useState(window.innerWidth < 1000);
  useEffect(() => {
    function handleResize() {
      setIsMobileUI(window.innerWidth < 1000);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navigate = useNavigate();
  const [currentActive, setCurrentActive] = useState(null);

  const [navs] = useState([
    // ── USER MANAGEMENT ─────────────────────────────────────────────
    {
      url: "/admin",
      name: "Admin",
      icon: "nav-icon fas fa-address-card",
      enum: "ADMIN",
      isPermissionEnabled: true,
      group: "USER_MANAGEMENT",
    },
    {
      url: "/sub_admin",
      name: "Sub Admin",
      icon: "nav-icon fas fa-address-card",
      enum: "SUB_ADMIN",
      isPermissionEnabled: true,
      group: "USER_MANAGEMENT",
    },
    {
      url: "/master",
      name: "MasterAgent Master",
      icon: "nav-icon fas fa-address-card",
      enum: "MASTER",
      isPermissionEnabled: true,
      group: "USER_MANAGEMENT",
    },
    {
      url: "/super_agent",
      name: "SuperAgent Master",
      icon: "nav-icon fas fa-address-card",
      enum: "SUPER_AGENT",
      isPermissionEnabled: true,
      group: "USER_MANAGEMENT",
    },
    {
      url: "/agent",
      name: "Agent Master",
      icon: "nav-icon fas fa-address-card",
      enum: "AGENT",
      isPermissionEnabled: true,
      group: "USER_MANAGEMENT",
    },
    {
      url: "/client",
      name: "Client Master",
      icon: "nav-icon fas fa-address-card",
      enum: "CLIENT",
      isPermissionEnabled: true,
      group: "USER_MANAGEMENT",
    },

    // ── GAMES ────────────────────────────────────────────────────────
    {
      url: "/game/inPlay",
      name: "InPlay Game",
      icon: "nav-icon fas fa-play",
      group: "GAMES",
    },
    {
      url: "/game/completeGame",
      name: "Complete Game",
      icon: "nav-icon far fa-stop-circle",
      group: "GAMES",
    },

    // ── CASINO ───────────────────────────────────────────────────────
    {
      name: "Live Casino Position",
      url: "#",
      icon: "nav-icon fas fa-chart-line",
      group: "CASINO",
      subNavs: [
        {
          url: "/casino/casino-position/TEEN_20",
          name: "Live Teen20 Position",
          icon: "nav-icon fas fa-chart-bar",
          requiresPassword: false,
        },
        // {
        //   url: "/casino/casino-position/DRAGON_TIGER_20",
        //   name: "Live DragonTiger Position",
        //   icon: "nav-icon fas fa-chart-bar",
        //   requiresPassword: false,
        // },
        // {
        //   url: "/casino/casino-position/AAA",
        //   name: "Amar Akbar Anthony Position",
        //   icon: "nav-icon fas fa-chart-bar",
        //   requiresPassword: false,
        // },
        {
          url: "/casino/casino-position/LUCKY7",
          name: "Live Lucky7 Position",
          icon: "nav-icon fas fa-chart-bar",
          requiresPassword: false,
        },
        {
          url: "/casino/casino-position/ABJ",
          name: "Live Andar Bahar Position",
          icon: "nav-icon fas fa-chart-bar",
          requiresPassword: false,
        },
        // {
        //   url: "/casino/casino-position/DRAGON_TIGER_202",
        //   name: "Live Dragon Tiger 2 Position",
        //   icon: "nav-icon fas fa-chart-bar",
        //   requiresPassword: false,
        // },
        // {
        //   url: "/casino/casino-position/WORLI2",
        //   name: "Live Worli Dashboard",
        //   icon: "nav-icon fas fa-chart-bar",
        //   requiresPassword: false,
        // },
      ],
    },
    {
      url: "/casino/casino-details",
      name: "Casino Details",
      icon: "nav-icon fas fa-chart-bar",
      group: "CASINO",
      requiresPassword: false,
    },
    {
      url: "/casino/casino-bet-report",
      name: "Casino Bet Report",
      icon: "nav-icon fas fa-chart-bar",
      group: "CASINO",
      requiresPassword: false,
    },

    {
      url: "/matka/inplay",
      name: "Matka Inplay",
      icon: "nav-icon fas fa-angle-right",
      enum: "CLIENT",
      isPermissionEnabled: true,
      group: "Matka",
    },
    {
      url: "/matka/complete",
      name: "Matka Complete",
      icon: "nav-icon fas fa-angle-right",
      enum: "CLIENT",
      isPermissionEnabled: true,
      group: "Matka",
    },

    // ── CASH TRANSACTION ─────────────────────────────────────────────
    {
      url: "/ct/client",
      name: "Debit/Credit Entry (C)",
      icon: "nav-icon fas fa-angle-right",
      enum: "CLIENT",
      isPermissionEnabled: true,
      group: "CASH_TRANSACTION",
    },
    {
      url: "/ct/agent",
      name: "Debit/Credit Entry (A)",
      icon: "nav-icon fas fa-angle-right",
      enum: "AGENT",
      isPermissionEnabled: true,
      group: "CASH_TRANSACTION",
    },
    {
      url: "/ct/super_agent",
      name: "Debit/Credit Entry (SA)",
      icon: "nav-icon fas fa-angle-right",
      enum: "SUPER_AGENT",
      isPermissionEnabled: true,
      group: "CASH_TRANSACTION",
    },
    {
      url: "/ct/master",
      name: "Debit/Credit Entry (M)",
      icon: "nav-icon fas fa-angle-right",
      enum: "MASTER",
      isPermissionEnabled: true,
      group: "CASH_TRANSACTION",
    },
    {
      url: "/ct/sub_admin",
      name: "Debit/Credit Entry (SUB)",
      icon: "nav-icon fas fa-angle-right",
      enum: "SUB_ADMIN",
      isPermissionEnabled: true,
      group: "CASH_TRANSACTION",
    },
    {
      url: "/ct/admin",
      name: "Debit/Credit Entry (AD)",
      icon: "nav-icon fas fa-angle-right",
      enum: "ADMIN",
      isPermissionEnabled: true,
      group: "CASH_TRANSACTION",
    },

    // ── LEDGER ───────────────────────────────────────────────────────
    {
      url: "/ledger",
      name: "My Ledger",
      icon: "nav-icon fas fa-angle-right",
      group: "LEDGER",
    },
    {
      url: "/ledger/client",
      name: "All Client Ledger",
      icon: "nav-icon fas fa-angle-right",
      enum: "CLIENT",
      isPermissionEnabled: true,
      group: "LEDGER",
    },
    {
      url: "/ledger/agent",
      name: "All Agent Ledger",
      icon: "nav-icon fas fa-angle-right",
      enum: "AGENT",
      isPermissionEnabled: true,
      group: "LEDGER",
    },
    {
      url: "/ledger/super",
      name: "All Super Ledger",
      icon: "nav-icon fas fa-angle-right",
      enum: "SUPER_AGENT",
      isPermissionEnabled: true,
      group: "LEDGER",
    },
    {
      url: "/ledger/master",
      name: "All Master Ledger",
      icon: "nav-icon fas fa-angle-right",
      enum: "MASTER",
      isPermissionEnabled: true,
      group: "LEDGER",
    },
    {
      url: "/ledger/subadmin",
      name: "All Sub Admin Ledger",
      icon: "nav-icon fas fa-angle-right",
      enum: "SUB_ADMIN",
      isPermissionEnabled: true,
      group: "LEDGER",
    },
    {
      url: "/ledger/admin",
      name: "All Admin Ledger",
      icon: "nav-icon fas fa-angle-right",
      enum: "ADMIN",
      isPermissionEnabled: true,
      group: "LEDGER",
    },
    {
      url: "/ledger/commission-lena-dena",
      name: "कमीशन लेन देन",
      icon: "nav-icon fas fa-angle-right",
      group: "LEDGER",
    },

    // ── REPORTS ──────────────────────────────────────────────────────
    {
      name: "Reports",
      url: "#",
      icon: "nav-icon fa fa-book",
      group: "REPORTS",
      subNavs: [
        {
          name: "Current Bets",
          url: "/report/current_bets",
          icon: "nav-icon fa fa-book",
        },
        {
          name: "Bet History",
          url: "/report/bet_history",
          icon: "nav-icon fa fa-book",
        },
      ],
    },

    // ── DATA REPORTS ─────────────────────────────────────────────────
    {
      url: "/data/report",
      name: "Data Reports",
      icon: "nav-icon fas fa-database",
      group: "DATA_REPORTS",
    },

    // ── LOGIN REPORTS ─────────────────────────────────────────────────
    {
      name: "Login Reports",
      url: "#",
      icon: "nav-icon fas fa-sign-in-alt",
      isPermissionEnabled: true,
      group: "LOGIN_REPORTS",
      subNavs: [
        { url: "/report/login/admin", name: "Admin Login Reports", enum: "ADMIN" },
        { url: "/report/login/sub_admin", name: "Sub Admin Login Reports", enum: "SUB_ADMIN" },
        { url: "/report/login/master", name: "Master Login Reports", enum: "MASTER" },
        { url: "/report/login/super_master", name: "Super Login Reports", enum: "SUPER_AGENT" },
        { url: "/report/login/agent", name: "Agent Login Reports", enum: "AGENT" },
        { url: "/report/login/client", name: "Clients Login Reports", enum: "CLIENT" },
      ],
    },
  ]);

  // ── Helpers ──────────────────────────────────────────────────────────
  const closeSideDrawer = () => {
    if (window.innerWidth < 768) {
      const body = document.body;
      body.classList.add("sidebar-closed", "sidebar-collapse");
      body.classList.remove("sidebar-open");
    }
  };

  const handleMenuToggle = () => {
    const isCurrentlyOpen = document.body.classList.contains("sidebar-open");
    if (isCurrentlyOpen) {
      document.body.classList.remove("sidebar-open");
      document.body.classList.add("sidebar-closed", "sidebar-collapse");
    } else {
      document.body.classList.add("sidebar-open");
      document.body.classList.remove("sidebar-closed", "sidebar-collapse");
    }
  };

  // ── Group navs ────────────────────────────────────────────────────────
  const groupedNavs = navs.reduce((acc, item) => {
    if (!acc[item.group]) acc[item.group] = [];
    acc[item.group].push(item);
    return acc;
  }, {});

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <aside className="main-sidebar sidebar-light-indigo elevation-4">
      {/* Brand Logo */}
      <a href="/" className="brand-link bg-indigo text-white">
        <img
          src="https://adminlte.io/themes/v3/dist/img/AdminLTELogo.png"
          alt="AdminLTE Logo"
          className="brand-image img-circle elevation-3"
          style={{ opacity: ".8" }}
        />
        <span className="brand-text font-weight-light" id="brandName">
          1EXCH99
        </span>
      </a>

      {isMobileUI && (
        <div className="crossButton">
          <button
            type="button"
            className="nav-link"
            style={{
              position: "absolute",
              top: "7px",
              zIndex: "00",
              right: "12px",
              width: "33px",
              color: "white",
            }}
            onClick={closeSideDrawer}
          >
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {/* Sidebar */}
      <div className="sidebar">
        <nav className="mt-2">
          <ul
            className="nav nav-pills nav-sidebar flex-column"
            data-widget="treeview"
            role="menu"
            data-accordion="false"
          >
            {Object.entries(groupedNavs).map(([groupName, items]) => {
              // Hide "Reports" group for non-SUPER_ADMIN users
              if (
                groupName.toUpperCase() === "REPORTS" &&
                userData?.userType?.toUpperCase() !== "SUPER_ADMIN"
              ) {
                return null;
              }

              return (
                <React.Fragment key={groupName}>
                  {/* ── Group header ── */}
                  <li className="nav-header">{groupName.replace(/_/g, " ")}</li>

                  {/* ── Group items ── */}
                  {items.map((item, index) => {
                    const activeKey = `${groupName}-${index}`;

                    // Hide permission-gated items that the current user can't access
                    if (
                      item.isPermissionEnabled &&
                      !PERMISSION_MAP[userData?.userType]?.includes(item.enum) &&
                      !item.subNavs
                    ) {
                      return null;
                    }

                    return (
                      <li
                        key={activeKey}
                        className={item.active ? "active nav-item" : "nav-item"}
                        onClick={() =>
                          setCurrentActive(
                            currentActive === activeKey ? null : activeKey
                          )
                        }
                      >
                        {/* ── Item link ── */}
                        <a
                          className={
                            location.pathname === item.url
                              ? "active nav-link"
                              : "nav-link"
                          }
                          href="javascript:00"
                          onClick={
                            item.url && !item.subNavs
                              ? () => handleNavClick(item.url, item.requiresPassword)
                              : undefined
                          }
                        >
                          <i className={item.icon} />
                          <p>{item.name}</p>
                          {item.subNavs && (
                            <i className="right fas fa-angle-left" />
                          )}
                        </a>

                        {/* ── Sub-nav ── */}
                        {item.subNavs && (
                          <ul
                            className={`nav nav-second-level ${currentActive === activeKey ? "" : "collapse"
                              }`}
                          >
                            {item.isPermissionEnabled
                              ? // Permission-gated sub-navs
                              item.subNavs.map(
                                (subnav) =>
                                  PERMISSION_MAP[userData?.userType]?.includes(
                                    subnav.enum
                                  ) && (
                                    <li className="nav-item" key={subnav.name}>
                                      <a
                                        className="nav-link"
                                        href="javascript:00"
                                        onClick={() =>
                                          handleNavClick(
                                            subnav.url,
                                            subnav.requiresPassword
                                          )
                                        }
                                      >
                                        <i className={subnav.icon} />
                                        {subnav.name}
                                      </a>
                                    </li>
                                  )
                              )
                              : // Open sub-navs (e.g. Casino positions)
                              item.subNavs.map((subnav) => (
                                <li className="nav-item" key={subnav.name}>
                                  <a
                                    className="nav-link"
                                    href="javascript:00"
                                    onClick={() =>
                                      handleNavClick(
                                        subnav.url,
                                        subnav.requiresPassword
                                      )
                                    }
                                  >
                                    <i className={subnav.icon} />
                                    {subnav.name}
                                  </a>
                                </li>
                              ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* ── Password modal (shared for ALL password-protected nav items) ── */}
      {showPasswordModal && userData?.userType === "SUPER_ADMIN" && (
        <PasswordCheckCasino
          show={showPasswordModal}
          setShow={setShowPasswordModal}
          targetUrl={pendingUrl}
          onSuccess={() => {
            navigate(pendingUrl);
            closeSideDrawer();
          }}
        />
      )}
    </aside>
  );
}